<template>
  <div class="container">
    <header>
      <h1>坐标点可视化</h1>
      <p class="subtitle">Y轴向上为正方向（数学坐标系）</p>
    </header>

    <div class="content">
      <div class="canvas-container">
        <canvas id="coordCanvas"></canvas>
      </div>

      <div class="controls">
        <div class="control-group">
          <label for="pointSize">点的大小</label>
          <input type="range" id="pointSize" min="2" max="10" value="2" @change="drawPoints">
        </div>

        <div class="control-group">
          <label for="pointColor">点的颜色</label>
          <input type="color" id="pointColor" value="#6a11cb" @change="drawPoints">
        </div>

        <div class="control-group">
          <label for="drawLines">连接线</label>
          <select id="drawLines" @change="drawPoints">
            <option value="none">不连接</option>
            <option value="sequential" selected>顺序连接</option>
            <option value="convex">凸包连接</option>
          </select>
        </div>

        <div class="control-group">
          <label for="showLabels">显示坐标</label>
          <select id="showLabels" @change="drawPoints">
            <option value="none">不显示</option>
            <option value="hover">悬停时显示</option>
            <option value="selected" selected>选中时显示</option>
          </select>
        </div>

        <div class="control-group">
          <label for="showGrid">显示网格</label>
          <select id="showGrid" @change="drawPoints">
            <option value="none">不显示</option>
            <option value="light">浅色网格</option>
            <option value="dark" selected>深色网格</option>
          </select>
        </div>

        <div class="control-group">
          <label for="showGrid">添加坐标点</label>
          <el-button @click="addPoint">添加坐标点</el-button>
        </div>
      </div>

      <div class="info">
        <h3>坐标系说明</h3>
        <p>当前使用<strong>数学坐标系</strong>：X轴向右为正方向，Y轴向上为正方向。这是标准数学和物理坐标系。</p>

        <div class="coordinate-system">
          <div>坐标系示例：</div>
          <div class="coordinate-example">(1247.328, 589.258)</div>
          <div>→ X=1247.328（右），Y=589.258（上）</div>
        </div>

        <div class="stats">
          <div class="stat-card">
            <div class="stat-value">74</div>
            <div class="stat-label">总点数</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ dataRangeX.toFixed(3) }}</div>
            <div class="stat-label">X轴范围</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ dataRangeY.toFixed(3) }}</div>
            <div class="stat-label">Y轴范围</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">(0, 589.258)</div>
            <div class="stat-label">右上角坐标</div>
          </div>
        </div>

        <div class="coordinates" id="coordList">
          <!-- 坐标数据将通过JavaScript填充 -->
          <span v-for="(coord, number) in coordinates" :key="number">
            <span class="coord-item">{{ coord[0] }}, {{ coord[1] }}</span>
          </span>
        </div>
      </div>
    </div>

    <footer>
      <p>坐标点可视化项目 &copy; 2023 | 使用HTML5 Canvas绘制 | Y轴向上为正方向</p>
    </footer>
  </div>
</template>

<script>
import mySocket from "@/components/mySocket";

// 添加边距
const padding = 50;
// 选中的点
let selectedPoint = null;

export default {
  name: "LaserPoint.vue",
  data () {
    return {
      coordinates:[[1247.328, 589.258], [1230.745, 376.508], [1188.328, 454.091], [1188.328, 427.841],],
      dataRangeX:0,
      dataRangeY:0,
      canvas: null,
      ctx: null,
      minX: Infinity,
      minY: Infinity,
      maxY: -Infinity,
      maxX: -Infinity,
    }
  },
  watch: {
  },
  methods: {
    addPoint () {
      this.coordinates.push([Math.random() * 1000, Math.random() * 1000]);
      this.updateCoordinates();
    },
    updateCoordinates () {
      // 计算坐标范围
      let minX = Infinity, maxX = -Infinity;
      let minY = Infinity, maxY = -Infinity;

      this.coordinates.forEach(coord => {
        minX = Math.min(minX, coord[0]);
        maxX = Math.max(maxX, coord[0]);
        minY = Math.min(minY, coord[1]);
        maxY = Math.max(maxY, coord[1]);
      });
      this.dataRangeX = maxX - minX;
      this.dataRangeY = maxY - minY;
    },
    resizeCanvasAndDrawPoints(){
      const container = this.canvas.parentElement;
      this.canvas.width = container.clientWidth;
      this.canvas.height = container.clientHeight;
      this.drawPoints();
    },
    drawPoints(){
      // 清除画布
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      // 获取设置
      const pointSize = parseInt(document.getElementById('pointSize').value);
      const pointColor = document.getElementById('pointColor').value;
      const drawLines = document.getElementById('drawLines').value;
      const showLabels = document.getElementById('showLabels').value;

      // 绘制网格
      this.drawGrid();

      // 绘制连接线（如果需要）
      if (drawLines === 'sequential') {
        this.ctx.beginPath();
        this.ctx.strokeStyle = 'rgba(106, 17, 203, 0.3)';
        this.ctx.lineWidth = 1;

        for (let i = 0; i < this.coordinates.length; i++) {
          const canvasCoords = this.getCanvasCoords(this.coordinates[i][0], this.coordinates[i][1]);

          if (i === 0) {
            this.ctx.moveTo(canvasCoords.x, canvasCoords.y);
          } else {
            this.ctx.lineTo(canvasCoords.x, canvasCoords.y);
          }
        }

        this.ctx.stroke();
      } else if (drawLines === 'convex') {
        // 简单的凸包算法
        const hull = this.calculateConvexHull(this.coordinates);

        this.ctx.beginPath();
        this.ctx.strokeStyle = 'rgba(37, 117, 252, 0.4)';
        this.ctx.lineWidth = 1.5;

        for (let i = 0; i < hull.length; i++) {
          const canvasCoords = this.getCanvasCoords(hull[i][0], hull[i][1]);

          if (i === 0) {
            this.ctx.moveTo(canvasCoords.x, canvasCoords.y);
          } else {
            this.ctx.lineTo(canvasCoords.x, canvasCoords.y);
          }
        }

        // 闭合凸包
        const firstPoint = this.getCanvasCoords(hull[0][0], hull[0][1]);
        this.ctx.lineTo(firstPoint.x, firstPoint.y);
        this.ctx.stroke();
      }

      // 绘制坐标点
      this.coordinates.forEach((coord, index) => {
        const canvasCoords = this.getCanvasCoords(coord[0], coord[1]);

        // 绘制点
        this.ctx.beginPath();
        this.ctx.fillStyle = pointColor;
        this.ctx.arc(canvasCoords.x, canvasCoords.y, pointSize, 0, Math.PI * 2);
        this.ctx.fill();

        // 添加白色边框
        this.ctx.beginPath();
        this.ctx.strokeStyle = 'white';
        this.ctx.lineWidth = 1;
        this.ctx.arc(canvasCoords.x, canvasCoords.y, pointSize, 0, Math.PI * 2);
        this.ctx.stroke();

        // 显示标签（如果需要）
        if (showLabels === 'selected' && selectedPoint === index) {
          this.drawPointLabel(coord, canvasCoords, index);
        }
      });

      // 绘制坐标轴
      this.drawAxes();
    },
    // 计算凸包
    calculateConvexHull(points) {
      if (points.length < 3) return points;

      // 找到最下面的点（如果有多个，取最左边的）
      let minYPoint = points[0];
      for (let i = 1; i < points.length; i++) {
        if (points[i][1] < minYPoint[1] ||
            (points[i][1] === minYPoint[1] && points[i][0] < minYPoint[0])) {
          minYPoint = points[i];
        }
      }

      // 按极角排序
      const sorted = points.slice().sort((a, b) => {
        const angleA = Math.atan2(a[1] - minYPoint[1], a[0] - minYPoint[0]);
        const angleB = Math.atan2(b[1] - minYPoint[1], b[0] - minYPoint[0]);

        if (angleA < angleB) return -1;
        if (angleA > angleB) return 1;

        // 如果角度相同，按距离排序
        const distA = Math.pow(a[0] - minYPoint[0], 2) + Math.pow(a[1] - minYPoint[1], 2);
        const distB = Math.pow(b[0] - minYPoint[0], 2) + Math.pow(b[1] - minYPoint[1], 2);
        return distA - distB;
      });

      // Graham扫描
      const hull = [sorted[0], sorted[1]];

      for (let i = 2; i < sorted.length; i++) {
        while (hull.length >= 2) {
          const p1 = hull[hull.length - 2];
          const p2 = hull[hull.length - 1];
          const p3 = sorted[i];

          // 计算叉积
          const cross = (p2[0] - p1[0]) * (p3[1] - p1[1]) - (p2[1] - p1[1]) * (p3[0] - p1[0]);

          if (cross <= 0) {
            hull.pop();
          } else {
            break;
          }
        }
        hull.push(sorted[i]);
      }

      return hull;
    },
    // 获取画布内的坐标 - 修正Y轴方向
    getCanvasCoords(x, y) {
      const scaleX = (this.canvas.width - 2 * padding) / this.dataRangeX;
      const scaleY = (this.canvas.height - 2 * padding) / this.dataRangeY;
      const scale = Math.min(scaleX, scaleY);

      const offsetX = (this.canvas.width - this.dataRangeX * scale) / 2;
      const offsetY = (this.canvas.height - this.dataRangeY * scale) / 2;

      // 关键修正：Y轴方向翻转，使向上为正方向
      return {
        x: offsetX + (x - this.minX) * scale,
        y: offsetY + (this.maxY - y) * scale  // 注意这里：maxY - y
      };
    },
    drawGrid() {
      const showGrid = document.getElementById('showGrid').value;
      if (showGrid === 'none') return;

      const gridColor = showGrid === 'light' ? 'rgba(200, 200, 200, 0.2)' : 'rgba(150, 150, 150, 0.3)';
      const gridLineWidth = showGrid === 'light' ? 0.5 : 1;

      this.ctx.strokeStyle = gridColor;
      this.ctx.lineWidth = gridLineWidth;

      // 计算网格线间隔
      const gridXCount = 10;
      const gridYCount = 10;
      const gridXStep = this.dataRangeX / gridXCount;
      const gridYStep = this.dataRangeY / gridYCount;

      // 绘制垂直线（X方向）
      for (let i = 0; i <= gridXCount; i++) {
        const xValue = this.minX + i * gridXStep;
        const canvasCoords = this.getCanvasCoords(xValue, this.minY);

        this.ctx.beginPath();
        this.ctx.moveTo(canvasCoords.x, this.getCanvasCoords(this.minX, this.minY).y);
        this.ctx.lineTo(canvasCoords.x, this.getCanvasCoords(this.minX, this.maxY).y);
        this.ctx.stroke();
      }

      // 绘制水平线（Y方向）
      for (let i = 0; i <= gridYCount; i++) {
        const yValue = this.minY + i * gridYStep;
        const canvasCoords = this.getCanvasCoords(this.minX, yValue);

        this.ctx.beginPath();
        this.ctx.moveTo(this.getCanvasCoords(this.minX, this.minY).x, canvasCoords.y);
        this.ctx.lineTo(this.getCanvasCoords(this.maxX, this.minY).x, canvasCoords.y);
        this.ctx.stroke();
      }
    },
    // 绘制坐标轴
    drawAxes() {
      const origin = this.getCanvasCoords(this.minX, 0);
      const rightEnd = this.getCanvasCoords(this.maxX, 0);
      const topEnd = this.getCanvasCoords(0, this.maxY);

      // 绘制X轴
      this.ctx.beginPath();
      this.ctx.strokeStyle = '#333';
      this.ctx.lineWidth = 2;
      this.ctx.moveTo(origin.x - 10, origin.y);
      this.ctx.lineTo(rightEnd.x + 10, origin.y);

      // 绘制X轴箭头
      this.ctx.lineTo(rightEnd.x + 5, origin.y - 5);
      this.ctx.moveTo(rightEnd.x + 10, origin.y);
      this.ctx.lineTo(rightEnd.x + 5, origin.y + 5);
      this.ctx.stroke();

      // 绘制Y轴
      this.ctx.beginPath();
      this.ctx.moveTo(origin.x, origin.y + 10);
      this.ctx.lineTo(origin.x, topEnd.y - 10);

      // 绘制Y轴箭头（注意方向，向上为正）
      this.ctx.lineTo(origin.x - 5, topEnd.y - 5);
      this.ctx.moveTo(origin.x, topEnd.y - 10);
      this.ctx.lineTo(origin.x + 5, topEnd.y - 5);
      this.ctx.stroke();

      // 绘制轴标签
      this.ctx.fillStyle = '#333';
      this.ctx.font = '14px Arial';
      this.ctx.textAlign = 'center';
      this.ctx.fillText('X轴', (origin.x + rightEnd.x) / 2, origin.y + 25);

      this.ctx.save();
      this.ctx.translate(origin.x - 25, (origin.y + topEnd.y) / 2);
      this.ctx.rotate(-Math.PI / 2);
      this.ctx.fillText('Y轴', 0, 0);
      this.ctx.restore();

      // 绘制刻度
      const xTicks = 8;
      const yTicks = 8;

      // X轴刻度
      this.ctx.font = '12px Arial';
      this.ctx.fillStyle = '#666';
      this.ctx.textAlign = 'center';

      for (let i = 0; i <= xTicks; i++) {
        const xValue = this.minX + (i / xTicks) * this.dataRangeX;
        const tickPos = this.getCanvasCoords(xValue, 0);

        this.ctx.beginPath();
        this.ctx.moveTo(tickPos.x, tickPos.y - 5);
        this.ctx.lineTo(tickPos.x, tickPos.y + 5);
        this.ctx.strokeStyle = '#666';
        this.ctx.lineWidth = 1;
        this.ctx.stroke();

        this.ctx.fillText(xValue.toFixed(0), tickPos.x, tickPos.y + 18);
      }

      // Y轴刻度
      this.ctx.textAlign = 'right';

      for (let i = 0; i <= yTicks; i++) {
        const yValue = this.minY + (i / yTicks) * this.dataRangeY;
        const tickPos = this.getCanvasCoords(0, yValue);

        this.ctx.beginPath();
        this.ctx.moveTo(tickPos.x - 5, tickPos.y);
        this.ctx.lineTo(tickPos.x + 5, tickPos.y);
        this.ctx.strokeStyle = '#666';
        this.ctx.lineWidth = 1;
        this.ctx.stroke();

        this.ctx.fillText(yValue.toFixed(0), tickPos.x - 8, tickPos.y + 4);
      }

      // 绘制原点标记
      this.ctx.beginPath();
      this.ctx.fillStyle = '#e74c3c';
      this.ctx.arc(origin.x, origin.y, 4, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.fillStyle = '#e74c3c';
      this.ctx.font = 'bold 12px Arial';
      this.ctx.textAlign = 'left';
      this.ctx.fillText('原点 (0,0)', origin.x + 8, origin.y - 8);
    },
    // 绘制点标签
    drawPointLabel(coord, canvasCoords, index) {
      this.ctx.fillStyle = '#2c3e50';
      this.ctx.font = '12px Arial';
      this.ctx.textAlign = 'left';
      this.ctx.fillText(`点${index+1}: (${coord[0].toFixed(2)}, ${coord[1].toFixed(2)})`, canvasCoords.x + 10, canvasCoords.y - 10);
    },
    async copyVBAString () {
      let vbaStr = '';
      for (let i in this.pointArr) {
        // Array(1261.325, 314.216),Array(1277.992, 317.216),Arra
        // POINT 1261.325,314.216
        // POINT 1277.992,317.216
        vbaStr += `POINT ${this.pointArr[i].x},${this.pointArr[i].y}\n`;
      }
      console.log(vbaStr)
      try {
        // 优先使用 Clipboard API（现代浏览器）
        await navigator.clipboard.writeText(vbaStr);
        // alert('复制成功！');
      } catch (err) {
        // 回退到 execCommand（适用于旧版浏览器如部分安卓 Edge）
        const textArea = document.createElement('textarea');
        textArea.value = vbaStr;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
        } catch (execErr) {
          console.error('复制失败:', execErr);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    },
  },
  mounted(){
    this.mySocket = mySocket.create({
      onmessage (msg) {
        console.log('msg', msg)
        let data = msg.type === 3 ? JSON.parse(msg.data) : false
        // {"title":"更新待加工任务","forward":true,"count":14}
        if (data && data.title === "更新待加工任务") {
          // remainingTasks = data.count
        }
        // {"title":"更新剩余加工时间","forward":true,"timeMs":1028189.1777777777}
        if (data && data.title === "更新剩余加工时间") {
          // remainingTime = Math.round(data.timeMs / 1000)
        }
        // {"title":"更新XY坐标","forward":true,"x":"286.572","y":"883.647"}
        if (data && data.title === "更新XY坐标") {
          self.x = data.x
          self.y = data.y
          self.time = data.time
        }
      }
    })
    // 原始坐标数据
    const coordinates = [
      [1247.328, 589.258], [1230.745, 376.508], [1188.328, 454.091], [1188.328, 427.841],
      [1199.495, 439.508], [1127.078, 303.591], [1102.745, 303.591], [1113.661, 283.174],
      [1127.911, 294.008], [1043.245, 311.758], [1060.245, 309.758], [1060.245, 331.924],
      [1044.328, 331.424], [1044.328, 459.924], [1138.745, 565.841], [1206.161, 640.591],
      [1172.685, 440.924], [1015.685, 373.341], [1006.352, 223.508], [997.268, 106.508],
      [1026.935, 83.758], [1115.185, 75.841], [1195.352, 67.258], [1174.935, 141.174],
      [1161.685, 119.674], [1150.685, 132.924], [1154.685, 142.424], [1111.852, 125.424],
      [1109.352, 116.008], [1096.768, 120.508], [1099.768, 129.258], [1047.935, 133.258],
      [1030.602, 135.258], [1030.602, 153.924], [1050.185, 153.924], [937.018, 61.508],
      [931.518, 222.258], [926.018, 357.508], [910.935, 390.174], [828.102, 480.424],
      [726.852, 611.501], [703.518, 576.174], [712.518, 335.258], [722.768, 61.758],
      [756.602, 20.341], [831.685, 22.341], [920.102, 26.341], [904.602, 80.758],
      [886.268, 69.008], [885.268, 97.258], [903.935, 90.258], [832.602, 71.091],
      [831.602, 56.924], [818.102, 50.924], [780.268, 72.591], [750.518, 72.591],
      [764.018, 93.758], [764.018, 61.924], [812.935, 235.508], [828.268, 236.508],
      [821.268, 245.424], [821.268, 230.258], [873.352, 251.424], [873.352, 271.258],
      [895.935, 269.758], [886.852, 247.258], [751.435, 400.501], [751.435, 370.174],
      [770.018, 383.758], [739.268, 383.758], [470.852, 383.758], [481.435, 214.008],
      [445.685, 162.424], [363.602, 155.924], [296.852, 150.758], [262.102, 179.258],
      [242.435, 450.008], [225.435, 683.258], [244.685, 768.091], [340.268, 772.591],
      [427.518, 776.591], [455.768, 643.341], [469.518, 422.924], [448.685, 298.758],
      [469.518, 296.758], [469.518, 276.841], [448.268, 274.341], [383.768, 234.174],
      [368.852, 219.424], [368.852, 241.424], [285.518, 256.841], [267.935, 280.174],
      [290.935, 280.174], [273.435, 279.174], [285.352, 262.758], [354.518, 485.674],
      [354.518, 454.841], [337.685, 470.501], [367.435, 470.501], [247.518, 601.091],
      [247.518, 584.508], [261.602, 504.758], [434.352, 617.508], [442.852, 603.174],
      [427.685, 603.174]
    ];

    // 计算坐标范围
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    coordinates.forEach(coord => {
      minX = Math.min(minX, coord[0]);
      maxX = Math.max(maxX, coord[0]);
      minY = Math.min(minY, coord[1]);
      maxY = Math.max(maxY, coord[1]);
    });

    // 添加边距
    // const dataRangeX = maxX - minX;
    // const dataRangeY = maxY - minY;

    // 在坐标列表中显示数据
    // const coordListElement = document.getElementById('coordList');
    // coordListElement.textContent = coordinates.map((coord) =>
    //     `POINT ${coord[0].toFixed(3)}, ${coord[1].toFixed(3)}`
    // ).join('\n');

    // 更新统计信息
    // document.querySelector('.stat-card:nth-child(2) .stat-value').textContent = dataRangeX.toFixed(3);
    // document.querySelector('.stat-card:nth-child(3) .stat-value').textContent = dataRangeY.toFixed(3);

    // Canvas绘制
    this.canvas = document.getElementById('coordCanvas');
    this.ctx = this.canvas.getContext('2d');

    // 设置Canvas尺寸
    // function resizeCanvas() {
    //   const container = canvas.parentElement;
    //   canvas.width = container.clientWidth;
    //   canvas.height = container.clientHeight;
    //   drawPoints();
    // }

    // 获取画布内的坐标 - 修正Y轴方向
    // function getCanvasCoords(x, y) {
    //   const scaleX = (canvas.width - 2 * padding) / dataRangeX;
    //   const scaleY = (canvas.height - 2 * padding) / dataRangeY;
    //   const scale = Math.min(scaleX, scaleY);
    //
    //   const offsetX = (canvas.width - dataRangeX * scale) / 2;
    //   const offsetY = (canvas.height - dataRangeY * scale) / 2;
    //
    //   // 关键修正：Y轴方向翻转，使向上为正方向
    //   return {
    //     x: offsetX + (x - minX) * scale,
    //     y: offsetY + (maxY - y) * scale  // 注意这里：maxY - y
    //   };
    // }

    // 绘制网格
    // function drawGrid() {
    //   const showGrid = document.getElementById('showGrid').value;
    //   if (showGrid === 'none') return;
    //
    //   const gridColor = showGrid === 'light' ? 'rgba(200, 200, 200, 0.2)' : 'rgba(150, 150, 150, 0.3)';
    //   const gridLineWidth = showGrid === 'light' ? 0.5 : 1;
    //
    //   ctx.strokeStyle = gridColor;
    //   ctx.lineWidth = gridLineWidth;
    //
    //   // 计算网格线间隔
    //   const gridXCount = 10;
    //   const gridYCount = 10;
    //   const gridXStep = dataRangeX / gridXCount;
    //   const gridYStep = dataRangeY / gridYCount;
    //
    //   // 绘制垂直线（X方向）
    //   for (let i = 0; i <= gridXCount; i++) {
    //     const xValue = minX + i * gridXStep;
    //     const canvasCoords = getCanvasCoords(xValue, minY);
    //
    //     ctx.beginPath();
    //     ctx.moveTo(canvasCoords.x, getCanvasCoords(minX, minY).y);
    //     ctx.lineTo(canvasCoords.x, getCanvasCoords(minX, maxY).y);
    //     ctx.stroke();
    //   }
    //
    //   // 绘制水平线（Y方向）
    //   for (let i = 0; i <= gridYCount; i++) {
    //     const yValue = minY + i * gridYStep;
    //     const canvasCoords = getCanvasCoords(minX, yValue);
    //
    //     ctx.beginPath();
    //     ctx.moveTo(getCanvasCoords(minX, minY).x, canvasCoords.y);
    //     ctx.lineTo(getCanvasCoords(maxX, minY).x, canvasCoords.y);
    //     ctx.stroke();
    //   }
    // }

    // 绘制点
    // function drawPoints() {
    //   // 清除画布
    //   ctx.clearRect(0, 0, canvas.width, canvas.height);
    //
    //   // 获取设置
    //   const pointSize = parseInt(document.getElementById('pointSize').value);
    //   const pointColor = document.getElementById('pointColor').value;
    //   const drawLines = document.getElementById('drawLines').value;
    //   const showLabels = document.getElementById('showLabels').value;
    //
    //   // 绘制网格
    //   drawGrid();
    //
    //   // 绘制连接线（如果需要）
    //   if (drawLines === 'sequential') {
    //     ctx.beginPath();
    //     ctx.strokeStyle = 'rgba(106, 17, 203, 0.3)';
    //     ctx.lineWidth = 1;
    //
    //     for (let i = 0; i < coordinates.length; i++) {
    //       const canvasCoords = getCanvasCoords(coordinates[i][0], coordinates[i][1]);
    //
    //       if (i === 0) {
    //         ctx.moveTo(canvasCoords.x, canvasCoords.y);
    //       } else {
    //         ctx.lineTo(canvasCoords.x, canvasCoords.y);
    //       }
    //     }
    //
    //     ctx.stroke();
    //   } else if (drawLines === 'convex') {
    //     // 简单的凸包算法
    //     const hull = calculateConvexHull(coordinates);
    //
    //     ctx.beginPath();
    //     ctx.strokeStyle = 'rgba(37, 117, 252, 0.4)';
    //     ctx.lineWidth = 1.5;
    //
    //     for (let i = 0; i < hull.length; i++) {
    //       const canvasCoords = getCanvasCoords(hull[i][0], hull[i][1]);
    //
    //       if (i === 0) {
    //         ctx.moveTo(canvasCoords.x, canvasCoords.y);
    //       } else {
    //         ctx.lineTo(canvasCoords.x, canvasCoords.y);
    //       }
    //     }
    //
    //     // 闭合凸包
    //     const firstPoint = getCanvasCoords(hull[0][0], hull[0][1]);
    //     ctx.lineTo(firstPoint.x, firstPoint.y);
    //     ctx.stroke();
    //   }
    //
    //   // 绘制坐标点
    //   coordinates.forEach((coord, index) => {
    //     const canvasCoords = getCanvasCoords(coord[0], coord[1]);
    //
    //     // 绘制点
    //     ctx.beginPath();
    //     ctx.fillStyle = pointColor;
    //     ctx.arc(canvasCoords.x, canvasCoords.y, pointSize, 0, Math.PI * 2);
    //     ctx.fill();
    //
    //     // 添加白色边框
    //     ctx.beginPath();
    //     ctx.strokeStyle = 'white';
    //     ctx.lineWidth = 1;
    //     ctx.arc(canvasCoords.x, canvasCoords.y, pointSize, 0, Math.PI * 2);
    //     ctx.stroke();
    //
    //     // 显示标签（如果需要）
    //     if (showLabels === 'selected' && selectedPoint === index) {
    //       drawPointLabel(coord, canvasCoords, index);
    //     }
    //   });
    //
    //   // 绘制坐标轴
    //   drawAxes();
    // }

    // 计算凸包（使用Graham扫描算法）
    // function calculateConvexHull(points) {
    //   if (points.length < 3) return points;
    //
    //   // 找到最下面的点（如果有多个，取最左边的）
    //   let minYPoint = points[0];
    //   for (let i = 1; i < points.length; i++) {
    //     if (points[i][1] < minYPoint[1] ||
    //         (points[i][1] === minYPoint[1] && points[i][0] < minYPoint[0])) {
    //       minYPoint = points[i];
    //     }
    //   }
    //
    //   // 按极角排序
    //   const sorted = points.slice().sort((a, b) => {
    //     const angleA = Math.atan2(a[1] - minYPoint[1], a[0] - minYPoint[0]);
    //     const angleB = Math.atan2(b[1] - minYPoint[1], b[0] - minYPoint[0]);
    //
    //     if (angleA < angleB) return -1;
    //     if (angleA > angleB) return 1;
    //
    //     // 如果角度相同，按距离排序
    //     const distA = Math.pow(a[0] - minYPoint[0], 2) + Math.pow(a[1] - minYPoint[1], 2);
    //     const distB = Math.pow(b[0] - minYPoint[0], 2) + Math.pow(b[1] - minYPoint[1], 2);
    //     return distA - distB;
    //   });
    //
    //   // Graham扫描
    //   const hull = [sorted[0], sorted[1]];
    //
    //   for (let i = 2; i < sorted.length; i++) {
    //     while (hull.length >= 2) {
    //       const p1 = hull[hull.length - 2];
    //       const p2 = hull[hull.length - 1];
    //       const p3 = sorted[i];
    //
    //       // 计算叉积
    //       const cross = (p2[0] - p1[0]) * (p3[1] - p1[1]) - (p2[1] - p1[1]) * (p3[0] - p1[0]);
    //
    //       if (cross <= 0) {
    //         hull.pop();
    //       } else {
    //         break;
    //       }
    //     }
    //     hull.push(sorted[i]);
    //   }
    //
    //   return hull;
    // }

    // 绘制坐标轴
    // function drawAxes() {
    //   const origin = getCanvasCoords(minX, 0);
    //   const rightEnd = getCanvasCoords(maxX, 0);
    //   const topEnd = getCanvasCoords(0, maxY);
    //
    //   // 绘制X轴
    //   ctx.beginPath();
    //   ctx.strokeStyle = '#333';
    //   ctx.lineWidth = 2;
    //   ctx.moveTo(origin.x - 10, origin.y);
    //   ctx.lineTo(rightEnd.x + 10, origin.y);
    //
    //   // 绘制X轴箭头
    //   ctx.lineTo(rightEnd.x + 5, origin.y - 5);
    //   ctx.moveTo(rightEnd.x + 10, origin.y);
    //   ctx.lineTo(rightEnd.x + 5, origin.y + 5);
    //   ctx.stroke();
    //
    //   // 绘制Y轴
    //   ctx.beginPath();
    //   ctx.moveTo(origin.x, origin.y + 10);
    //   ctx.lineTo(origin.x, topEnd.y - 10);
    //
    //   // 绘制Y轴箭头（注意方向，向上为正）
    //   ctx.lineTo(origin.x - 5, topEnd.y - 5);
    //   ctx.moveTo(origin.x, topEnd.y - 10);
    //   ctx.lineTo(origin.x + 5, topEnd.y - 5);
    //   ctx.stroke();
    //
    //   // 绘制轴标签
    //   ctx.fillStyle = '#333';
    //   ctx.font = '14px Arial';
    //   ctx.textAlign = 'center';
    //   ctx.fillText('X轴', (origin.x + rightEnd.x) / 2, origin.y + 25);
    //
    //   ctx.save();
    //   ctx.translate(origin.x - 25, (origin.y + topEnd.y) / 2);
    //   ctx.rotate(-Math.PI / 2);
    //   ctx.fillText('Y轴', 0, 0);
    //   ctx.restore();
    //
    //   // 绘制刻度
    //   const xTicks = 8;
    //   const yTicks = 8;
    //
    //   // X轴刻度
    //   ctx.font = '12px Arial';
    //   ctx.fillStyle = '#666';
    //   ctx.textAlign = 'center';
    //
    //   for (let i = 0; i <= xTicks; i++) {
    //     const xValue = minX + (i / xTicks) * dataRangeX;
    //     const tickPos = getCanvasCoords(xValue, 0);
    //
    //     ctx.beginPath();
    //     ctx.moveTo(tickPos.x, tickPos.y - 5);
    //     ctx.lineTo(tickPos.x, tickPos.y + 5);
    //     ctx.strokeStyle = '#666';
    //     ctx.lineWidth = 1;
    //     ctx.stroke();
    //
    //     ctx.fillText(xValue.toFixed(0), tickPos.x, tickPos.y + 18);
    //   }
    //
    //   // Y轴刻度
    //   ctx.textAlign = 'right';
    //
    //   for (let i = 0; i <= yTicks; i++) {
    //     const yValue = minY + (i / yTicks) * dataRangeY;
    //     const tickPos = getCanvasCoords(0, yValue);
    //
    //     ctx.beginPath();
    //     ctx.moveTo(tickPos.x - 5, tickPos.y);
    //     ctx.lineTo(tickPos.x + 5, tickPos.y);
    //     ctx.strokeStyle = '#666';
    //     ctx.lineWidth = 1;
    //     ctx.stroke();
    //
    //     ctx.fillText(yValue.toFixed(0), tickPos.x - 8, tickPos.y + 4);
    //   }
    //
    //   // 绘制原点标记
    //   ctx.beginPath();
    //   ctx.fillStyle = '#e74c3c';
    //   ctx.arc(origin.x, origin.y, 4, 0, Math.PI * 2);
    //   ctx.fill();
    //   ctx.fillStyle = '#e74c3c';
    //   ctx.font = 'bold 12px Arial';
    //   ctx.textAlign = 'left';
    //   ctx.fillText('原点 (0,0)', origin.x + 8, origin.y - 8);
    // }

    // // 绘制点标签
    // function drawPointLabel(coord, canvasCoords, index) {
    //   ctx.fillStyle = '#2c3e50';
    //   ctx.font = '12px Arial';
    //   ctx.textAlign = 'left';
    //   ctx.fillText(`点${index+1}: (${coord[0].toFixed(2)}, ${coord[1].toFixed(2)})`, canvasCoords.x + 10, canvasCoords.y - 10);
    // }

    // 鼠标悬停事件
    this.canvas.addEventListener('mousemove', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const showLabels = document.getElementById('showLabels').value;

      // 检查是否悬停在点上
      let hoveredPoint = null;
      const pointSize = parseInt(document.getElementById('pointSize').value);

      for (let i = 0; i < coordinates.length; i++) {
        const canvasCoords = this.getCanvasCoords(coordinates[i][0], coordinates[i][1]);
        const distance = Math.sqrt(Math.pow(mouseX - canvasCoords.x, 2) + Math.pow(mouseY - canvasCoords.y, 2));

        if (distance <= pointSize + 3) {
          hoveredPoint = i;
          break;
        }
      }

      // 如果悬停模式开启且悬停在点上，重新绘制并显示标签
      if (showLabels === 'hover' && hoveredPoint !== null) {
        this.drawPoints();

        const coord = coordinates[hoveredPoint];
        const canvasCoords = this.getCanvasCoords(coord[0], coord[1]);

        this.ctx.fillStyle = '#e74c3c';
        this.ctx.font = 'bold 14px Arial';
        this.ctx.textAlign = 'left';
        this.ctx.fillText(`点${hoveredPoint+1}: (${coord[0].toFixed(2)}, ${coord[1].toFixed(2)})`, canvasCoords.x + 10, canvasCoords.y - 10);

        // 绘制一个高亮点
        this.ctx.beginPath();
        this.ctx.fillStyle = '#e74c3c';
        this.ctx.arc(canvasCoords.x, canvasCoords.y, pointSize + 2, 0, Math.PI * 2);
        this.ctx.fill();

        this.ctx.beginPath();
        this.ctx.fillStyle = document.getElementById('pointColor').value;
        this.ctx.arc(canvasCoords.x, canvasCoords.y, pointSize, 0, Math.PI * 2);
        this.ctx.fill();
      }
    });

    // 点击事件
    this.canvas.addEventListener('click', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const pointSize = parseInt(document.getElementById('pointSize').value);

      for (let i = 0; i < coordinates.length; i++) {
        const canvasCoords = this.getCanvasCoords(coordinates[i][0], coordinates[i][1]);
        const distance = Math.sqrt(Math.pow(mouseX - canvasCoords.x, 2) + Math.pow(mouseY - canvasCoords.y, 2));

        if (distance <= pointSize + 3) {
          selectedPoint = (selectedPoint === i) ? null : i;
          this.drawPoints();
          break;
        }
      }
    });

    // 初始化
    window.addEventListener('load', () => {
      this.resizeCanvas();
      window.addEventListener('resize', this.resizeCanvas);
    });

    // 控件事件监听
    // document.getElementById('pointSize').addEventListener('input', this.drawPoints);
    // document.getElementById('pointColor').addEventListener('input', drawPoints);
    // document.getElementById('drawLines').addEventListener('change', drawPoints);
    // document.getElementById('showLabels').addEventListener('change', drawPoints);
    // document.getElementById('showGrid').addEventListener('change', drawPoints);
  }
}
</script>
<style>
.container {
  max-width: 1200px;
  margin: 0 auto;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
header {
  background: linear-gradient(135deg, #6a11cb 0%, #2575fc 100%);
  color: white;
  padding: 25px 30px;
  text-align: center;
}
h1 {
  font-size: 2.2rem;
  margin-bottom: 10px;
}
.subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
}
.content {
  padding: 25px;
  display: flex;
  flex-direction: column;
  gap: 25px;
}
.canvas-container {
  position: relative;
  border: 1px solid #e1e5eb;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f9fafc;
}
canvas {
  display: block;
  width: 100%;
  height: 600px;
  cursor: crosshair;
}
.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}
.control-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
label {
  font-weight: 600;
  font-size: 0.95rem;
}
select, input {
  padding: 10px 15px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 1rem;
  background-color: white;
}
select:focus, input:focus {
  outline: none;
  border-color: #6a11cb;
  box-shadow: 0 0 0 3px rgba(106, 17, 203, 0.1);
}
.info {
  background-color: #e8f4fd;
  border-left: 4px solid #2196f3;
  padding: 18px;
  border-radius: 6px;
  margin-top: 10px;
}
.info h3 {
  color: #0d47a1;
  margin-bottom: 10px;
}
.coordinates {
  font-family: monospace;
  font-size: 0.9rem;
  white-space: pre-wrap;
  background-color: #f1f3f4;
  padding: 15px;
  border-radius: 6px;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 10px;
}
.stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 15px;
}
.stat-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 15px;
  border-radius: 8px;
  text-align: center;
}
.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2c3e50;
}
.stat-label {
  font-size: 0.9rem;
  color: #7f8c8d;
  margin-top: 5px;
}
.coordinate-system {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding: 10px;
  background-color: #f0f7ff;
  border-radius: 6px;
}
.coordinate-example {
  font-family: monospace;
  background-color: #2c3e50;
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
}
footer {
  text-align: center;
  padding: 20px;
  color: #7f8c8d;
  font-size: 0.9rem;
  border-top: 1px solid #eee;
  margin-top: 20px;
}
@media (max-width: 768px) {
  .content {
    padding: 15px;
    width: calc(100% - 30px);
  }
  canvas {
    height: 500px;
  }
  .controls {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>